import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import useLuxPlusRenewalBonus, { getCurrentRenewalRewardStep } from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import useLoyaltyEnrolExistingUser from 'loyalty/hooks/useLoyaltyEnrolExistingUser'
import { RenewalTextSkeleton } from '../LandingPage/LuxPlusRenewalBonusTimeline'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useCallback, type ReactNode } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

interface Props {
  item: App.MemberSubscriptionItem
}

const messages = defineMessages({
  title: {
    id: 'HB6UJjaADS4BM6nkchmX',
    defaultMessage: 'Your renewal has been cancelled',
    description: 'Title confirming the members renewal has been cancelled',
  },
  thankYou: {
    id: 'p3Dt68CC5U7cODYCYLB5',
    defaultMessage: 'Thank you for being a part of {programName}.',
    description: 'Thank-you line on the renewal cancelled confirmation screen',
  },
  bodyWithBonus: {
    id: '8mj8hlZR5gZCooA4AUzP',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind, renew your membership and receive your bonus {loyaltyPoints} and {statusCredits}.',
    description: 'Reminder that the member can still renew to receive their bonus points and status credits',
  },
  bodyWithPoints: {
    id: 'S_iXb5K_TKwoNyayRQft',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind, renew your membership and receive your bonus {loyaltyPoints}.',
    description: 'Reminder that the member can still renew to receive their bonus points',
  },
  bodyNoBonus: {
    id: 'qzwGSBf0BG7v8nymvqpb',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind and renew your membership.',
    description: 'Reminder that the member can still change their mind and renew before the membership year ends',
  },
  joinBonusFull: {
    id: 'tjY0rg4Yzv1eIxKe4Mvb',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind. Join <link>Société</link> for free and renew your membership to receive a renewal bonus of {loyaltyPoints} and {statusCredits}.',
    description: 'Cancel-renewal confirmation telling a non-Société member to join and renew before their membership year ends to receive their renewal bonus',
  },
  joinBonusPoints: {
    id: '_6SeKWX6hZKvWQj0lLE-',
    defaultMessage: 'You have up to the end of your current membership year ending <b>{date}</b> to change your mind. Join <link>Société</link> for free and renew your membership to receive a renewal bonus of {loyaltyPoints}.',
    description: 'Cancel-renewal confirmation telling a non-Société member to join and renew before their membership year ends to receive bonus points',
  },
  close: {
    id: 'sO7IRSznzFiwdtTnKZ4x',
    defaultMessage: 'Close',
    description: 'Button to dismiss the renewal cancelled confirmation screen',
  },
})

/**
 * Redesigned renewal-cancellation confirmation ("Your renewal has been cancelled"), shown when the
 * LuxPlus+ tiered renewal reward experience is enabled. The pre-flag design lives in
 * {@link LuxPlusManageAccountModalRenewalCancellationSuccess}.
 */
function LuxPlusManageAccountModalRenewalCancellationSuccessTiered({ item }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const joinSociete = useLoyaltyEnrolExistingUser()
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const { steps, isLoading } = useLuxPlusRenewalBonus()

  const nextReward = getCurrentRenewalRewardStep(steps)
  const points = nextReward?.points
  const statusCredits = nextReward?.statusCredits

  const hasBonusAmounts = points !== undefined
  const showStatusCredits = hasBonusAmounts && !hideStatusCredits && statusCredits !== undefined
  // Wait for the reward amounts before committing to a copy variant, so the no-bonus sentence
  // does not flash and reflow into the bonus sentence once the fetch resolves.
  const isAwaitingBonusAmounts = isLoading

  const date = <FormatDateTime value={item.expiryDate} format="dmy-casual" />
  const b = (chunks: Array<ReactNode>) => <b>{chunks}</b>

  return <>
    <ModalHeader title={intl.formatMessage(messages.title)} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" colour="light-foreground-alt">
            {intl.formatMessage(messages.thankYou, { programName: LUXURY_PLUS.PROGRAM_NAME })}
          </BodyText>
          {isAwaitingBonusAmounts
            ? <RenewalTextSkeleton lineWidths={[320, 300, 160]} height={18} />
            : <BodyText variant="medium" colour="light-foreground-alt">
                {points !== undefined && isSocieteMember && showStatusCredits && statusCredits !== undefined && <FormattedMessage
                  {...messages.bodyWithBonus}
                  values={{
                    date,
                    b,
                    loyaltyPoints: <FormatLoyaltyPoints points={points} />,
                    statusCredits: <FormatLoyaltyStatusCredits statusCredits={statusCredits} />,
                  }}
                />}
                {points !== undefined && isSocieteMember && !showStatusCredits && <FormattedMessage
                  {...messages.bodyWithPoints}
                  values={{ date, b, loyaltyPoints: <FormatLoyaltyPoints points={points} /> }}
                />}
                {points !== undefined && !isSocieteMember && showStatusCredits && statusCredits !== undefined && <FormattedMessage
                  {...messages.joinBonusFull}
                  values={{
                    date,
                    b,
                    link: (chunks) => <TextLink weight="regular" onClick={joinSociete}>{chunks}</TextLink>,
                    loyaltyPoints: <FormatLoyaltyPoints points={points} />,
                    statusCredits: <FormatLoyaltyStatusCredits statusCredits={statusCredits} />,
                  }}
                />}
                {points !== undefined && !isSocieteMember && !showStatusCredits && <FormattedMessage
                  {...messages.joinBonusPoints}
                  values={{
                    date,
                    b,
                    link: (chunks) => <TextLink weight="regular" onClick={joinSociete}>{chunks}</TextLink>,
                    loyaltyPoints: <FormatLoyaltyPoints points={points} />,
                  }}
                />}
                {points === undefined && <FormattedMessage {...messages.bodyNoBonus} values={{ date, b }} />}
            </BodyText>}
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      secondaryActionProps={{ children: intl.formatMessage(messages.close), onClick: dismissModal }}
    />
  </>
}

export default LuxPlusManageAccountModalRenewalCancellationSuccessTiered
